import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  booleanAttribute,
  numberAttribute,
} from '@angular/core';
import { DsButtonV2Component } from '../buttons/ds-button-v2.component';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsTabOption, DsTabsComponent } from '../navigation/ds-tabs.component';
import { DsProgressBarComponent } from '../progress/ds-progress-bar.component';

export interface DsCarouselSlide {
  value: string;
  label: string;
  imageSrc: string;
  imageAlt?: string;
  icon?: string;
  title: string;
  body: string;
  strongText?: string;
  buttonLabel?: string;
  textCard?: boolean;
  textTone?: 'dark' | 'light';
}

@Component({
  selector: 'ds-carousel',
  standalone: true,
  imports: [
    DsButtonV2Component,
    DsIconButtonV2Component,
    DsProgressBarComponent,
    DsTabsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (slides.length > 0 && activeSlide; as slide) {
      <section
        class="ds-carousel"
        [class.ds-carousel--paused]="isPaused"
        [style.--ds-carousel-max-width]="maxWidth"
        [attr.aria-roledescription]="'carousel'"
        [attr.aria-label]="ariaLabel"
      >
        <div class="ds-carousel__viewport">
          @if (slide.imageSrc) {
            <img
              class="ds-carousel__image"
              [src]="slide.imageSrc"
              [alt]="slide.imageAlt || ''"
              [style.--ds-carousel-image-scale]="imageScale"
            />
          }
          @if (nextSlide?.imageSrc) {
            <img
              class="ds-carousel__preload-image"
              [src]="nextSlide?.imageSrc"
              alt=""
              aria-hidden="true"
            />
          }

          <div class="ds-carousel__content">
            <div
              class="ds-carousel__copy"
              [class.ds-carousel__copy--fade-a]="copyAnimationPhase % 2 === 0"
              [class.ds-carousel__copy--fade-b]="copyAnimationPhase % 2 !== 0"
              [class.ds-carousel__copy--card]="slide.textCard === true"
              [class.ds-carousel__copy--light]="slide.textTone === 'light'"
            >
              <div class="ds-carousel__text">
                <p class="ds-carousel__title">{{ slide.title }}</p>
                <p class="ds-carousel__body">
                  {{ slide.body }}
                  @if (slide.strongText) {
                    <strong>{{ slide.strongText }}</strong>
                  }
                </p>
              </div>

              @if (slide.buttonLabel) {
                <ds-button-v2
                  [label]="slide.buttonLabel"
                  size="xs"
                  emphasis="primary"
                  [invert]="true"
                  (clicked)="ctaClicked.emit(slide)"
                />
              }
            </div>
          </div>
        </div>

        <ds-tabs
          [tabs]="tabOptions"
          [value]="activeValue"
          size="m"
          (valueChange)="selectValue($event)"
        />

        <div class="ds-carousel__progress-row">
          <ds-progress-bar
            class="ds-carousel__progress"
            [value]="progress"
            [ariaLabel]="progressAriaLabel"
          />
          <ds-icon-button-v2
            [icon]="isPaused ? 'PlayCircleFill' : 'PauseCircleFill'"
            size="s"
            emphasis="primary"
            [ariaLabel]="isPaused ? playAriaLabel : pauseAriaLabel"
            (clicked)="togglePlayback()"
          />
        </div>
      </section>
    }
  `,
  styles: [`
    :host {
      display: block;
      max-width: 100%;
    }

    .ds-carousel {
      display: flex;
      flex-direction: column;
      gap: var(--ds-carousel-stack-gap);
      width: min(100%, var(--ds-carousel-max-width, var(--ds-carousel-width)));
    }

    .ds-carousel__viewport {
      position: relative;
      display: flex;
      align-items: center;
      min-height: var(--ds-carousel-slide-height);
      padding: var(--ds-carousel-slide-padding-y) var(--ds-carousel-slide-padding-x);
      box-sizing: border-box;
      overflow: hidden;
      border-radius: var(--ds-carousel-slide-border-radius);
      background: var(--ds-carousel-fallback-background);
      box-shadow: var(--ds-shadow-l2);
      isolation: isolate;
    }

    .ds-carousel__image {
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      z-index: -1;
      height: 100%;
      width: auto;
      max-width: none;
      transform-origin: center center;
      transform: translateZ(0) scale(var(--ds-carousel-image-scale, 1));
      will-change: transform;
    }

    .ds-carousel__preload-image {
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
      overflow: hidden;
    }

    .ds-carousel__content {
      display: flex;
      align-items: flex-start;
      width: 100%;
      min-width: 0;
    }

    .ds-carousel__copy {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: var(--ds-carousel-content-gap);
      width: min(100%, var(--ds-carousel-text-card-width));
      box-sizing: border-box;
      color: var(--ds-carousel-text-color);
    }

    .ds-carousel__copy--fade-a {
      animation: ds-carousel-copy-fade-a 200ms ease-out 100ms both;
    }

    .ds-carousel__copy--fade-b {
      animation: ds-carousel-copy-fade-b 200ms ease-out 100ms both;
    }

    .ds-carousel__copy--card {
      padding: var(--ds-carousel-text-card-padding);
      border-radius: var(--ds-carousel-text-card-border-radius);
      background: var(--ds-carousel-text-card-background);
      backdrop-filter: blur(var(--ds-carousel-text-card-blur));
      -webkit-backdrop-filter: blur(var(--ds-carousel-text-card-blur));
    }

    .ds-carousel__copy--light {
      color: var(--ds-carousel-text-inverse-color);
    }

    .ds-carousel__text {
      width: 100%;
      min-width: 0;
    }

    .ds-carousel__title,
    .ds-carousel__body {
      margin: 0;
      color: inherit;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-carousel-body-font-size);
      letter-spacing: 0;
    }

    .ds-carousel__title {
      font-size: var(--ds-carousel-title-font-size);
      font-weight: var(--ds-carousel-title-font-weight);
      line-height: var(--ds-carousel-title-line-height);
    }

    .ds-carousel__body {
      font-weight: var(--ds-carousel-body-font-weight);
      line-height: var(--ds-carousel-body-line-height);
    }

    .ds-carousel__body strong {
      font-weight: var(--ds-carousel-body-strong-font-weight);
    }

    .ds-carousel__progress-row {
      display: flex;
      align-items: center;
      gap: var(--ds-carousel-progress-controls-gap);
      width: 100%;
    }

    .ds-carousel__progress {
      flex: 1 1 auto;
      min-width: 0;
    }

    @media (max-width: 720px) {
      .ds-carousel__viewport {
        align-items: flex-end;
        min-height: 360px;
        padding: var(--ds-carousel-text-card-padding);
      }

      .ds-carousel__copy {
        width: 100%;
      }

      .ds-carousel__progress-row {
        gap: var(--ds-carousel-stack-gap);
      }
    }

    @keyframes ds-carousel-copy-fade-a {
      from {
        opacity: 0;
      }

      to {
        opacity: 1;
      }
    }

    @keyframes ds-carousel-copy-fade-b {
      from {
        opacity: 0;
      }

      to {
        opacity: 1;
      }
    }

  `],
})
export class DsCarouselComponent implements OnInit, OnChanges, OnDestroy {
  @Input() slides: DsCarouselSlide[] = [];
  @Input() value = '';
  @Input({ transform: numberAttribute }) durationMs = 15000;
  @Input({ transform: booleanAttribute }) autoPlay = true;
  @Input({ transform: booleanAttribute }) paused = false;
  @Input() ariaLabel = 'Carrusel';
  @Input() progressAriaLabel = 'Tiempo restante de la diapositiva';
  @Input() pauseAriaLabel = 'Pausar carrusel';
  @Input() playAriaLabel = 'Reproducir carrusel';
  @Input() maxWidth = 'var(--ds-carousel-width)';

  @Output() valueChange = new EventEmitter<string>();
  @Output() slideChange = new EventEmitter<DsCarouselSlide>();
  @Output() ctaClicked = new EventEmitter<DsCarouselSlide>();
  @Output() pausedChange = new EventEmitter<boolean>();

  progress = 0;
  copyAnimationPhase = 0;

  private animationFrameId: number | null = null;
  private slideStartedAt = 0;
  private pausedAt = 0;
  private elapsedBeforePause = 0;
  private slidesSignature = '';
  private readonly preloadedImageSrcs = new Set<string>();

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.ensureActiveValue();
    this.restartTimer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const slidesChanged = changes['slides'] ? this.hasSlidesChanged() : false;
    const shouldRestart =
      slidesChanged ||
      !!changes['value'] ||
      !!changes['durationMs'] ||
      !!changes['autoPlay'];

    if (slidesChanged || changes['value']) {
      this.ensureActiveValue();
    }

    if (shouldRestart) {
      this.restartTimer();
    }

    if (changes['paused'] && !shouldRestart) {
      this.syncPausedState();
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  get activeSlide(): DsCarouselSlide | undefined {
    return this.slides.find((slide) => slide.value === this.activeValue) ?? this.slides[0];
  }

  get activeValue(): string {
    return this.value || this.slides[0]?.value || '';
  }

  get nextSlide(): DsCarouselSlide | undefined {
    if (this.slides.length <= 1) return undefined;
    const currentIndex = this.slides.findIndex((slide) => slide.value === this.activeValue);
    const nextIndex = (Math.max(0, currentIndex) + 1) % this.slides.length;
    return this.slides[nextIndex];
  }

  get tabOptions(): DsTabOption[] {
    return this.slides.map((slide) => ({
      label: slide.label,
      value: slide.value,
      icon: slide.icon,
    }));
  }

  get isPaused(): boolean {
    return this.paused || !this.autoPlay;
  }

  get imageScale(): string {
    return (1 + (this.progress / 100) * 0.09).toFixed(4);
  }

  selectValue(value: string): void {
    if (value === this.activeValue) return;
    this.value = value;
    this.valueChange.emit(value);
    this.emitActiveSlide();
    this.restartTimer();
  }

  togglePlayback(): void {
    this.paused = !this.paused;
    this.pausedChange.emit(this.paused);
    this.syncPausedState();
  }

  private ensureActiveValue(): void {
    if (!this.slides.length) {
      this.value = '';
      return;
    }

    if (!this.slides.some((slide) => slide.value === this.value)) {
      this.value = this.slides[0].value;
      this.valueChange.emit(this.value);
    }
  }

  private restartTimer(): void {
    this.clearTimer();
    this.progress = 0;
    this.copyAnimationPhase += 1;
    this.elapsedBeforePause = 0;
    this.pausedAt = 0;
    this.slideStartedAt = Date.now();
    this.preloadAdjacentImage();

    if (!this.autoPlay || this.paused || this.slides.length <= 1) {
      this.changeDetector.markForCheck();
      return;
    }

    this.scheduleTick();
    this.changeDetector.markForCheck();
  }

  private syncPausedState(): void {
    if (!this.autoPlay || this.slides.length <= 1) {
      this.clearTimer();
      this.changeDetector.markForCheck();
      return;
    }

    if (this.paused) {
      if (this.animationFrameId !== null) {
        this.elapsedBeforePause += Date.now() - this.slideStartedAt;
        this.pausedAt = Date.now();
      }
      this.clearTimer();
      this.changeDetector.markForCheck();
      return;
    }

    if (this.pausedAt) {
      this.slideStartedAt = Date.now();
      this.pausedAt = 0;
    }

    if (this.animationFrameId === null) {
      this.scheduleTick();
    }
  }

  private tick(): void {
    this.animationFrameId = null;
    const duration = Math.max(1, this.durationMs);
    const elapsed = this.elapsedBeforePause + Date.now() - this.slideStartedAt;
    this.progress = Math.min(100, (elapsed / duration) * 100);

    if (elapsed >= duration) {
      this.goToNextSlide();
      return;
    }

    this.changeDetector.markForCheck();
    this.scheduleTick();
  }

  private goToNextSlide(): void {
    const currentIndex = this.slides.findIndex((slide) => slide.value === this.activeValue);
    const nextIndex = (Math.max(0, currentIndex) + 1) % this.slides.length;
    this.value = this.slides[nextIndex].value;
    this.valueChange.emit(this.value);
    this.emitActiveSlide();
    this.restartTimer();
  }

  private emitActiveSlide(): void {
    const slide = this.activeSlide;
    if (slide) {
      this.slideChange.emit(slide);
    }
  }

  private clearTimer(): void {
    if (this.animationFrameId !== null) {
      window.cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private scheduleTick(): void {
    this.animationFrameId = window.requestAnimationFrame(() => this.tick());
  }

  private preloadAdjacentImage(): void {
    const src = this.nextSlide?.imageSrc;
    if (!src || this.preloadedImageSrcs.has(src) || typeof Image === 'undefined') {
      return;
    }

    this.preloadedImageSrcs.add(src);
    const image = new Image();
    image.src = src;
  }

  private hasSlidesChanged(): boolean {
    const nextSignature = this.slides
      .map((slide) => [
        slide.value,
        slide.label,
        slide.imageSrc,
        slide.icon ?? '',
        slide.title,
        slide.body,
        slide.strongText ?? '',
        slide.buttonLabel ?? '',
        slide.textCard === true,
        slide.textTone ?? 'dark',
      ].join('\u001f'))
      .join('\u001e');

    if (nextSignature === this.slidesSignature) {
      return false;
    }

    this.slidesSignature = nextSignature;
    return true;
  }
}
